import { useEffect, useMemo, useRef, useState } from 'react';

import { lazyLoad } from '../embed-playlist/helper/lazyLoader';
import { useGlobalStore } from '../lead-capture-form/state/globalStore';
import { ENTITY_TYPE } from '../lead-capture-form/utils/constants';
import { isMobile } from './utils/helpers';

const InteractivityTool = lazyLoad(
  () => import('./components/InteractivityTool'),
  () => <div />,
);

const getParentContainer = () => {
  const elementById = document.getElementById('parent_container') as HTMLElement;
  if (!elementById) {
    return;
  }
  return elementById;
};

const getParentContainerWidth = (parentContainerRef: HTMLElement | undefined): number => {
  const boundingRectangle = parentContainerRef?.getBoundingClientRect();
  return boundingRectangle ? boundingRectangle.width : -1;
};

const getToolbarAttachPosition = (
  parentContainerRef: HTMLElement | undefined,
): 'bottom' | 'right' => {
  const boundingRectangle = parentContainerRef?.getBoundingClientRect();
  const containerHeight = boundingRectangle ? boundingRectangle.height : -1;
  const containerWidth = getParentContainerWidth(parentContainerRef);
  return containerWidth < containerHeight ? 'bottom' : 'right';
};

const DESKTOP_MIN_WIDTH = 800;

const InteractionTool = () => {
  const parentContainerRef = useRef(getParentContainer());

  const [toolbarAttachPosition, setToolbarAttachPosition] = useState(
    getToolbarAttachPosition(parentContainerRef.current),
  );

  const entityType = useGlobalStore((store) => store.entityType);
  const sessionType = 'live_event';
  const sessionId = useGlobalStore.getState().eventId;
  const containerWidth = getParentContainerWidth(parentContainerRef.current);
  const isMobileView = containerWidth <= DESKTOP_MIN_WIDTH || isMobile();

  const isInteractivityToolsAllowed = useMemo(() => {
    return entityType === ENTITY_TYPE.EVENT;
  }, [entityType]);

  useEffect(() => {
    const onResize = () => {
      const toolbarNewAttachPosition = getToolbarAttachPosition(parentContainerRef.current);
      if (toolbarNewAttachPosition !== toolbarAttachPosition) {
        setToolbarAttachPosition(toolbarNewAttachPosition);
      }
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [toolbarAttachPosition, isMobileView, containerWidth]);

  if (parentContainerRef.current && isInteractivityToolsAllowed) {
    return (
      <InteractivityTool
        isMobileView={isMobileView}
        parentContainerRef={parentContainerRef.current}
        sessionId={sessionId}
        sessionType={sessionType}
        toolbarAttachPosition={toolbarAttachPosition}
      />
    );
  }

  return null;
};

export default InteractionTool;
